import React, { useState, useEffect } from 'react';
import { Button, Grid, LinearProgress, Typography } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import stepIcon from '../../../assets/registration/input_pin.png'
//import RoundedButton from '../../../components/Actions/RoundedButton';
import useInterval from '../../../hooks/useInterval';
import {makeStyles} from '@mui/styles'
import ContainedButton from '../../../components/actions/ContainedButton';
import DisplayError from '../../../components/alerts/DisplayError';
import PhoneCodeInput from '../../../components/forms/PhoneCodeInput';

/**
 * @module Layouts/Registration/views/SMSValidation
 * @description This is a functional component in JavaScript React that displays a form for SMS validation. It takes
in four props: `form`, `actions`, `system`, and `data`. It uses the `useStyles` hook from
Material-UI to define CSS styles for the component. It also uses the `useState` hook to define a
state variable `count` and a function `setCount` to update it. It uses a custom hook `useInterval`
to set up an interval that calls a function `onUpdateCounter` every second to update the `count`
state. It defines two functions `onUpdateCounter` and `onResendCode` to update the `count` state and
trigger an action to resend an SMS code. It calculates the progress of a linear progress bar based
on the `count` state. It returns a JSX element that displays the form for SMS validation, including
an image, a title, a phone code input, an error message, a continue button, a countdown timer, a
linear progress bar, and a resend button.
 * @returns {React.ReactElement} The `SMSValidation` component is being returned as a View.
 */

/**
 * 
 * @typedef SMSValidation
 * @property {MUIStyles} classes - The styles for the component 
 * @property {Boolean} loading - Indicates if initialization process is taking place
 * @property {Boolean} sending - Indicates if a process has started
 * @property {Object | String | Boolean} error - Variable that handles error, it can be a boolean, a string or an object 

 * @property {Number} pincode - Pin Number from SMS
 * @property {Function} onValidateSMSCodev2 - This function validates the entered code number to continue the enrollment process

 */


const SMSValidation = ({form, actions, system, data}) => {

    const classes = useStyles()
    const [count, setCount] = useState(59)

    /* `useInterval` is a custom hook that sets up an interval that calls a given function at a
    specified interval (in this case, every 1000 milliseconds or 1 second). In this component, it is
    used to update the countdown timer displayed to the user for how long they have to wait before
    they can request a new SMS code. The `onUpdateCounter` function is called every second to update
    the `count` state, which is then used to calculate the progress of the linear progress bar and
    display the remaining time to the user. */
    useInterval(() => {
        onUpdateCounter()
    }, 1000)

    /**
     * @function
     * @description The function decrements the count state by 1 if it is greater than 0, otherwise it sets it to 0.
     * @param count - Variable that contains the remaining time to resend a new code
     */
    const onUpdateCounter = () => {
        const _count = count > 0 ? count - 1 : 0
        setCount(_count)
    }

    /**
     * @function 
     * @description The function resets a countdown timer and triggers an action to resend an SMS code. Also resets the count 
     */
    const onResendCode = () => {
        setCount(60)
        actions.onSendSMSv2()
        // Resend actions dispatch
    }

    let phonenumber = form.country_code_area.value 
    if(form.cell_phone_number.value) phonenumber = `${phonenumber} ${form.cell_phone_number.value}`

    const progress = Math.ceil((count/60)*100)

    return ( 
        <div className={classes.root}>
            <Grid container spacing={3} >
                <Grid item xs={12}>
                    <div className={classes.image_container}>
                        <img src={stepIcon} alt='' width='100%'/>
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant='h6' align='center' className={classes.title}>
                        SMS Validation
                    </Typography>
                    <Typography variant='body1' align='center' className={classes.text}>
                        Code has been sent, please enter it as soon as you receive it.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Grid container justifyContent='center'>
                        <Grid item >
                            <div style={{display:'inline-block', margin:'auto'}}>
                                <PhoneCodeInput value={data.phoneCode} onChange={actions.onUpdateCode}/>
                            </div>
                        </Grid>
                    </Grid>
                    
                </Grid>
                <Grid item xs={12}>
                    <DisplayError>{system.error}</DisplayError>
                    <ContainedButton loading={system.sending} fullWidth color='primary'
                        onClick={actions.onValidateSMSCodev2}
                    >Continue</ContainedButton>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant='body2' align='center' className={classes.text}>
                        {`A SMS was sent to ${phonenumber}`}
                    </Typography>
                    <Typography variant='body2' align='center' className={classes.text}>
                        {`You can request a new code in ${count} seconds`}
                    </Typography>
                    <div style={{marginTop:16, marginBottom:16}}>
                        <LinearProgress variant='determinate' value={progress}/>
                    </div>
                    <Grid container justifyContent='center'>
                        <Grid item>
                            <Button disabled={count > 0} color='primary' className={classes.button}
                                onClick={onResendCode}
                            >Resend code</Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </div>
     );
}

const useStyles = makeStyles(theme => ({
    root:{
        width:'100%',
        maxWidth:400,
        margin:'auto',
        padding:'40px 0px'
    },
    image_container:{
        width:200,
        margin:'auto',
        [theme.breakpoints.down('sm')]:{
            width:150,
        }
    },
    title:{
        fontWeight:600,
        lineHeight:1.5,
    },
    text:{
        fontWeight:500,
        color:blueGrey[400],
        [theme.breakpoints.down('sm')]:{
            fontSize:'0.875rem'
        }
    },
    button:{
        color:theme.palette.primary.dark,
        textTransform:'uppercase'
    }
}))
 
export default SMSValidation;